import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { TimeRange } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const VALUE_REPORT_CLUSTER_LIST_QUERY_KEY = 'value-report-cluster-list';

type GetValueReportClusterListQueryKeyArgs = {
  clusterIds: string[] | undefined;
  key: string;
  pageLimit: number;
  useListingPrices: boolean;
  managedOnly?: boolean;
};

const getValueReportClusterListQueryKey = ({
  clusterIds,
  key,
  pageLimit,
  useListingPrices,
  managedOnly,
}: GetValueReportClusterListQueryKeyArgs) =>
  [
    VALUE_REPORT_CLUSTER_LIST_QUERY_KEY,
    clusterIds,
    key,
    pageLimit,
    useListingPrices,
    managedOnly,
  ] as const;

type UseValueReportClusterListQueryArgs = {
  timeZone: string;
  clusterIds?: string[];
  key: string;
  getRange: () => TimeRange;
  enabled?: boolean;
  pageLimit?: number;
  useListingPrices?: boolean;
  managedOnly?: boolean;
};

const getValueReportClusterListQueryOptions = (
  {
    clusterIds,
    key,
    getRange,
    timeZone,
    useListingPrices = false,
    managedOnly,
    pageLimit = 1000,
    enabled = true,
  }: UseValueReportClusterListQueryArgs,
  organizationId: string | undefined
) =>
  infiniteQueryOptions({
    queryKey: getValueReportClusterListQueryKey({
      clusterIds,
      key,
      pageLimit,
      useListingPrices,
      managedOnly,
    }),
    queryFn: async ({ pageParam }) => {
      const range = getRange();

      const { data } = await apiClient.costReport.getValueReportClusterList(
        {
          organizationId: organizationId ?? '',
          startTime: range.from,
          endTime: range.to,
          timeZone,
          useListingPrices,
          managedOnly,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          runClustersValueRealizationReportRequestFilter: { clusterIds },
        },
        { timeout: 120_000 }
      );
      return data;
    },
    enabled: enabled && !!organizationId,
    initialPageParam: '',
    getNextPageParam: (res) => {
      return res.nextPageCursor === '' ? undefined : res.nextPageCursor;
    },
    select: (data) => data.pages.flatMap((page) => page.items ?? []),
  });

export const useValueReportClusterListQuery = (
  args: UseValueReportClusterListQueryArgs
) => {
  const { currentOrganization } = useOrganizations();

  return useInfiniteQuery(
    getValueReportClusterListQueryOptions(args, currentOrganization?.id)
  );
};
